// Chakra imports
import {
  Box,
  Text,
  useColorModeValue,
  Icon,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  Button,
  Badge,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Switch,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useToast,
} from "@chakra-ui/react";
// Custom components
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MdArrowBack, MdEmail, MdPerson, MdCheckCircle, MdCancel, MdEdit, MdDelete } from "react-icons/md";
import { formatDate } from "utils/format";
import systemService from "services/systemService";

export default function ViewRecipient() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Chakra Color Mode - All hooks must be called at the top level
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'white');
  const cardBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('secondaryGray.200', 'whiteAlpha.100');
  const badgeBg = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recipient, setRecipient] = useState(null);
  
  // Edit State
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [recipientFormData, setRecipientFormData] = useState({ email: '', name: '', is_active: true });
  const [formErrors, setFormErrors] = useState({});
  
  const toast = useToast();
  
  // Modals
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = useRef();

  useEffect(() => {
    const fetchRecipient = async () => {
      if (!id) {
        setError('Recipient ID is required');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const result = await systemService.getMonitoringRecipient(id);
        if (result.success && result.data) {
          setRecipient(result.data);
        } else {
          setError(result.error || 'Failed to load recipient');
        }
      } catch (err) {
        console.error('Error fetching recipient:', err);
        setError('Failed to load recipient. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchRecipient();
  }, [id]);

  // Handle open edit modal
  const handleOpenEdit = () => {
    if (recipient) {
      setRecipientFormData({
        email: recipient.email || '',
        name: recipient.name || '',
        is_active: recipient.is_active !== undefined ? recipient.is_active : true,
      });
      setFormErrors({});
      onEditOpen();
    }
  };

  // Handle update recipient
  const handleUpdateRecipient = async () => {
    // Validate
    const errors = {};
    if (!recipientFormData.email || !recipientFormData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientFormData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!recipientFormData.name || !recipientFormData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setUpdating(true);
    setFormErrors({});
    
    try {
      const result = await systemService.updateMonitoringRecipient(id, {
        email: recipientFormData.email.trim(),
        name: recipientFormData.name.trim(),
        is_active: recipientFormData.is_active,
      });
      
      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Recipient updated successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        // Update local state
        setRecipient(result.data);
        onEditClose();
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to update recipient',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Error updating recipient:', err);
      toast({
        title: 'Error',
        description: 'Failed to update recipient. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete recipient
  const handleDeleteRecipient = async () => {
    setDeleting(true);
    try {
      const result = await systemService.deleteMonitoringRecipient(id);
      
      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Recipient deleted successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onDeleteClose();
        // Navigate back to monitoring page
        navigate('/admin/monitoring');
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to delete recipient',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Error deleting recipient:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete recipient. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Alert status="error" borderRadius="15px" mb="20px">
          <AlertIcon />
          <Text color={textColor}>{error}</Text>
        </Alert>
        <Button
          leftIcon={<Icon as={MdArrowBack} />}
          onClick={() => navigate('/admin/monitoring')}
          colorScheme="blue">
          Back to Monitoring
        </Button>
      </Box>
    );
  }

  if (!recipient) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Alert status="info" borderRadius="15px" mb="20px">
          <AlertIcon />
          <Text color={textColor}>Recipient not found</Text>
        </Alert>
        <Button
          leftIcon={<Icon as={MdArrowBack} />}
          onClick={() => navigate('/admin/monitoring')}
          colorScheme="blue">
          Back to Monitoring
        </Button>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex direction='column' gap='20px'>
        {/* Header */}
        <Flex align='center' justify='space-between' flexWrap='wrap' gap='15px'>
          <Flex align='center' gap='15px'>
            <Button
              leftIcon={<Icon as={MdArrowBack} />}
              onClick={() => navigate('/admin/monitoring')}
              variant='ghost'
              size='sm'>
              Back
            </Button>
            <Text color={textColor} fontSize='2xl' fontWeight='700'>
              Recipient Details
            </Text>
          </Flex>
          <Flex gap='10px'>
            <Button
              leftIcon={<Icon as={MdEdit} />}
              onClick={handleOpenEdit}
              colorScheme='blue'
              size='sm'>
              Edit Recipient
            </Button>
            <Button
              leftIcon={<Icon as={MdDelete} />}
              onClick={onDeleteOpen}
              colorScheme='red'
              size='sm'>
              Delete Recipient
            </Button>
          </Flex>
        </Flex>

        {/* Recipient Information Card */}
        <Card p='30px' bg={cardBg}>
          <Flex align='center' gap='10px' mb='20px'>
            <Icon as={MdPerson} color={textColor} fontSize='xl' />
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              Recipient Information
            </Text>
          </Flex>
          <Divider borderColor={borderColor} mb='20px' />
          
          <Flex direction='column' gap='20px'>
            {/* Name */}
            <Box>
              <Flex align='center' gap='10px' mb='5px'>
                <Icon as={MdPerson} color={textColorSecondary} fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm' fontWeight='500'>
                  Name
                </Text>
              </Flex>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {recipient.name || 'N/A'}
              </Text>
            </Box>

            {/* Email */}
            <Box>
              <Flex align='center' gap='10px' mb='5px'>
                <Icon as={MdEmail} color={textColorSecondary} fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm' fontWeight='500'>
                  Email
                </Text>
              </Flex>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {recipient.email}
              </Text>
            </Box>

            {/* Status */}
            <Box>
              <Flex align='center' gap='10px' mb='5px'>
                <Icon 
                  as={recipient.is_active ? MdCheckCircle : MdCancel} 
                  color={textColorSecondary} 
                  fontSize='sm' 
                />
                <Text color={textColorSecondary} fontSize='sm' fontWeight='500'>
                  Status
                </Text>
              </Flex>
              <Badge
                colorScheme={recipient.is_active ? 'green' : 'red'}
                bg={badgeBg}
                color={textColor}
                fontSize='sm'
                fontWeight='600'
                px='12px'
                py='6px'
                borderRadius='8px'
              >
                {recipient.is_active ? 'ACTIVE' : 'INACTIVE'}
              </Badge>
            </Box>

            {/* Created At */}
            {recipient.created_at && (
              <Box>
                <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='5px'>
                  Created At
                </Text>
                <Text color={textColor} fontSize='md'>
                  {formatDate(recipient.created_at)}
                </Text>
              </Box>
            )}

            {/* Updated At */}
            {recipient.updated_at && (
              <Box>
                <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='5px'>
                  Last Updated
                </Text>
                <Text color={textColor} fontSize='md'>
                  {formatDate(recipient.updated_at)}
                </Text>
              </Box>
            )}

            {/* ID */}
            <Box>
              <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='5px'>
                Recipient ID
              </Text>
              <Text color={textColor} fontSize='sm' fontFamily='mono'>
                {recipient.id}
              </Text>
            </Box>
          </Flex>
        </Card>
      </Flex>

      {/* Edit Recipient Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="md">
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={textColor}>Edit Report Recipient</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap="20px">
              <FormControl isRequired isInvalid={!!formErrors.name}>
                <FormLabel color={textColor}>Name</FormLabel>
                <Input
                  type="text"
                  value={recipientFormData.name}
                  onChange={(e) => {
                    setRecipientFormData({ ...recipientFormData, name: e.target.value });
                    if (formErrors.name) {
                      setFormErrors({ ...formErrors, name: '' });
                    }
                  }}
                  placeholder="Enter recipient name"
                  bg={badgeBg}
                  color={textColor}
                />
                {formErrors.name && (
                  <Text color="red.500" fontSize="xs" mt="5px">
                    {formErrors.name}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired isInvalid={!!formErrors.email}>
                <FormLabel color={textColor}>Email</FormLabel>
                <Input
                  type="email"
                  value={recipientFormData.email}
                  onChange={(e) => {
                    setRecipientFormData({ ...recipientFormData, email: e.target.value });
                    if (formErrors.email) {
                      setFormErrors({ ...formErrors, email: '' });
                    }
                  }}
                  placeholder="Enter recipient email"
                  bg={badgeBg}
                  color={textColor}
                />
                {formErrors.email && (
                  <Text color="red.500" fontSize="xs" mt="5px">
                    {formErrors.email}
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <Flex align="center" justify="space-between">
                  <FormLabel color={textColor} mb="0">
                    Active Status
                  </FormLabel>
                  <Switch
                    isChecked={recipientFormData.is_active}
                    onChange={(e) => setRecipientFormData({ ...recipientFormData, is_active: e.target.checked })}
                    colorScheme="green"
                  />
                </Flex>
                <Text color={textColorSecondary} fontSize="xs" mt="5px">
                  Recipient will receive monitoring reports when active
                </Text>
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdateRecipient}
              isLoading={updating}
              loadingText="Updating">
              Update Recipient
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={cardBg}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={textColor}>
              <Flex align="center" gap="10px">
                <Icon as={MdDelete} color="red.500" fontSize="xl" />
                Delete Recipient
              </Flex>
            </AlertDialogHeader>
            <AlertDialogBody color={textColor}>
              <Text>
                Are you sure you want to delete this recipient? This action cannot be undone.
              </Text>
              {recipient && (
                <Box mt="15px" p="15px" bg={badgeBg} borderRadius="8px">
                  <Text fontSize="sm" fontWeight="600" mb="5px">Recipient Details:</Text>
                  <Text fontSize="sm">Name: {recipient.name}</Text>
                  <Text fontSize="sm">Email: {recipient.email}</Text>
                </Box>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteRecipient}
                ml={3}
                isLoading={deleting}
                loadingText="Deleting"
                leftIcon={<Icon as={MdDelete} />}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

